import type { PodcastInfo } from './api'

type PodcastStatus = PodcastInfo['status']

export type PodcastSurfaceState = {
  podcast_status?: PodcastStatus | null
  podcast_audio_ready?: boolean | null
  podcast_duration_seconds?: number | null
  podcast_error_message?: string | null
}

export type PodcastReportPayload = PodcastSurfaceState & {
  podcast?: Partial<PodcastInfo> | null
}

export type HydratedPodcastSurfaceState = {
  podcast_status?: PodcastStatus
  podcast_audio_ready?: boolean
  podcast_duration_seconds?: number
  podcast_error_message?: string
}

function resolveHydratedPodcastStatus(input: {
  status?: PodcastStatus
  audioReady?: boolean
  errorMessage?: string
}): PodcastStatus | undefined {
  if (input.audioReady) {
    return 'ready'
  }
  if (input.status === 'ready') {
    return input.errorMessage ? 'failed' : 'missing'
  }
  return input.status
}

export function hydratePodcastSurfaceState(
  existing: PodcastSurfaceState,
  reportData: PodcastReportPayload
): HydratedPodcastSurfaceState {
  const nestedPodcast = reportData.podcast || {}
  const serverStatus = reportData.podcast_status ?? nestedPodcast.status ?? undefined
  const existingStatus = existing.podcast_status ?? undefined
  const hasServerStatus = Boolean(serverStatus)
  const isServerReady = reportData.podcast_audio_ready === true || Boolean(nestedPodcast.audio_url)
  const resolvedAudioReady = isServerReady
    ? true
    : hasServerStatus
      ? false
      : Boolean(existing.podcast_audio_ready)
  const resolvedDurationSeconds =
    reportData.podcast_duration_seconds ??
    nestedPodcast.duration_seconds ??
    existing.podcast_duration_seconds ??
    undefined
  const resolvedErrorMessage =
    reportData.podcast_error_message ??
    nestedPodcast.error_message ??
    existing.podcast_error_message ??
    undefined
  const resolvedStatus = resolveHydratedPodcastStatus({
    status: serverStatus ?? existingStatus,
    audioReady: resolvedAudioReady,
    errorMessage: resolvedErrorMessage,
  })

  return {
    podcast_status: resolvedStatus,
    podcast_audio_ready: resolvedAudioReady,
    podcast_duration_seconds: resolvedDurationSeconds ?? undefined,
    podcast_error_message: resolvedErrorMessage ?? undefined,
  }
}
